/**
 * router/AppRouter.tsx
 * desc: root router component
 * note: converts "routes.ts" configuration into Router components
 */

import { Suspense, type ComponentType, type ReactNode } from 'react';
import { RouterProvider } from "react-router/dom";
import { createBrowserRouter, Outlet, Route, createRoutesFromElements } from 'react-router';
import AuthorizedRoute from './AuthorizedRoute';
import ConfirmDialog from '../components/ConfirmDialog';
import LayoutUntyped from '../components/Layout';
import routes from '../routes';
import { useSelector } from 'react-redux';
import * as Sentry from "@sentry/react";
import GlobalErrorUntyped from '../modules/GlobalError';
import type { CurrentUser, RouteExtraProps } from './types';

// unconverted JS components infer every destructured prop as required
const Layout = LayoutUntyped as unknown as ComponentType<{ children?: ReactNode }>;
const GlobalError = GlobalErrorUntyped as unknown as ComponentType<{ error?: unknown }>;

// Sentry renders `fallback` as a component (createElement), so it has to be a
// stable module-level definition or it remounts on every AppRouter render
const renderErrorFallback = ({ error }: { error: unknown }) => <GlobalError error={ error } />;

// the slice of store state the router reads — the store itself is still
// untyped JS, so this interface is the boundary contract
interface RouterStoreState {
    users: {
        me: CurrentUser;
    };
}

const AppRouter = () => {
    const user = useSelector((state: RouterStoreState) => state.users.me);

    const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

    const renderRoutes = (extraProps: RouteExtraProps) => {

        const appRoutes = routes.appRoutes.map((route, i) => {
            return <Route
                key={ route.key || i }
                path={ route.path }
                element={ <Sentry.ErrorBoundary fallback={ renderErrorFallback }>
                    <Suspense fallback={ <span></span> }>
                        <AuthorizedRoute route={ route } extraProps={ extraProps } />
                    </Suspense>
                </Sentry.ErrorBoundary> }
            />;
        });
    
        const isolatedRoutes = routes.isolatedRoutes.map((route, i) => {
            return <Route
                key={ route.key || i }
                path={ route.path }
                element={ <Sentry.ErrorBoundary fallback={ renderErrorFallback }>
                    <Suspense fallback={ <span></span> }>
                        <AuthorizedRoute route={ route } extraProps={ extraProps } /></Suspense>
                </Sentry.ErrorBoundary> }
            />;
        });
         
    
        return <Route element={ <><Outlet/><ConfirmDialog/></> }>
            <Route
                path="/"
                element={ <Sentry.ErrorBoundary fallback={ renderErrorFallback }><Layout/></Sentry.ErrorBoundary> }
                errorElement={ <GlobalError /> }
            >
                { appRoutes }
            </Route>
            { isolatedRoutes }
        </Route>;
    };

    const router = sentryCreateBrowserRouter(
        createRoutesFromElements(
            renderRoutes({ user })
        )
    );
    return <RouterProvider router={ router } />;
};

export default AppRouter;
